import { useState } from 'react';
import { gql, useApolloClient, useLazyQuery } from '@apollo/client';
import GET_ID_PROOFING from 'client/queries/idProofing/getIdProofing';
import { ID_PROOFING_ENTITY_TYPES } from 'routes/marketplace-ede/constants/id-verify/index';
import { getEdeQueryName, useEdeQueryName } from 'routes/marketplace-ede/context/edeQueryProvider';
import { EXCHANGES, Exchange } from 'routes/marketplace-ede/constants/exchange';

type IdProofingQuery = {
  getIdProofing: {
    entityId: string;
    cmsProofingId: string;
    methodType: string;
  };
};

type IdProofingVariables = {
  entityId: string;
  entityType: string;
};

type RegrantPermissionMutation =
  | {
      grantPermission: {
        grantedPermission: boolean;
      };
    }
  | {
      grantGaepPermission: {
        grantedPermission: boolean;
      };
    };

type RegrantPermissionVariables = {
  insuranceApplicationIdentifier: string;
  payload: {
    identityProofingChannelType: string;
    identityProofingIdentifier: string | null;
  };
};

type RegrantBrokerPermissionsMutationArgs = {
  appId: number;
  exchange?: Exchange;
};

type RegrantPermissionsMutationArgs = RegrantBrokerPermissionsMutationArgs & {
  personId: string;
};

export const REGRANT_PERMISSION = (queryName: string) => gql`
  mutation RegrantPermission($insuranceApplicationIdentifier: ID!, $payload: PermissionInput!) {
    ${queryName}(appID: $insuranceApplicationIdentifier, payload: $payload) {
      grantedPermission
    }
  }
`;

const useRegrantPermissionMutation = () => {
  const client = useApolloClient();
  const basePermissionQueryName = useEdeQueryName('GRANT_PERMISSION') as string;
  const [loading, setLoading] = useState(true);
  const [getIdProofing] = useLazyQuery<IdProofingQuery, IdProofingVariables>(GET_ID_PROOFING);

  const regrantPermissionMutation = async ({ personId, appId, exchange }: RegrantPermissionsMutationArgs) => {
    const permissionQueryName = exchange
      ? getEdeQueryName('GRANT_PERMISSION', exchange)
      : basePermissionQueryName;

    try {
      const { data: idProofing } = await getIdProofing({
        variables: {
          entityId: personId,
          entityType: ID_PROOFING_ENTITY_TYPES.CONSUMER,
        },
        context: {
          headers: {
            'x-ede-exchange': exchange,
          },
        },
      });

      if (!idProofing?.getIdProofing) {
        throw new Error('User has not completed ID proofing.');
      }

      const payload = {
        identityProofingChannelType: idProofing.getIdProofing.methodType,
        identityProofingIdentifier: idProofing.getIdProofing.cmsProofingId,
      };

      const { data: grantPermissionData } = await client.mutate<
        RegrantPermissionMutation,
        RegrantPermissionVariables
      >({
        mutation: REGRANT_PERMISSION(permissionQueryName),
        variables: {
          insuranceApplicationIdentifier: appId.toString(),
          payload,
        },
      });

      return {
        success: grantPermissionData?.[permissionQueryName].grantedPermission,
        error: undefined,
      };
    } catch (e) {
      return {
        success: false,
        error: e.message,
      };
    } finally {
      setLoading(false);
    }
  };

  const regrantBrokerPermissionMutation = async ({
    appId,
    exchange,
  }: RegrantBrokerPermissionsMutationArgs) => {
    const permissionQueryName = exchange
      ? getEdeQueryName('GRANT_PERMISSION', exchange)
      : basePermissionQueryName;
    try {
      const payload = {
        identityProofingChannelType: 'TELEPHONE',
        identityProofingIdentifier: null,
      };

      const { data: grantPermissionData } = await client.mutate<
        RegrantPermissionMutation,
        RegrantPermissionVariables
      >({
        mutation: REGRANT_PERMISSION(permissionQueryName),
        variables: {
          insuranceApplicationIdentifier: appId.toString(),
          payload,
        },
      });

      return {
        success: grantPermissionData?.[permissionQueryName].grantedPermission,
        error: undefined,
      };
    } catch (e) {
      return {
        success: false,
        error: e.message,
      };
    } finally {
      setLoading(false);
    }
  };

  return {
    regrantPermissionMutation,
    regrantBrokerPermissionMutation,
    loading,
  };
};

export default useRegrantPermissionMutation;
